<script setup>
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { useDashboardStore } from '@/stores/dashboard'

const props = defineProps({
  dashboard: {
    type: Object,
    required: true
  },
  isSubscribed: {
    type: Boolean,
    default: false
  },
  onFork: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['modal-close'])
const toast = useToast()
const dashboardStore = useDashboardStore()
const forking = ref(false)
const keepSubscription = ref(props.isSubscribed)

const widgets = computed(() => props.dashboard.widgets || [])

const uniqueSavedSearches = computed(() => {
  const seen = new Set()
  const result = []
  for (const w of widgets.value) {
    if (w.saved_search && !seen.has(w.saved_search.id)) {
      seen.add(w.saved_search.id)
      result.push(w.saved_search)
    }
  }
  return result
})

async function doFork() {
  if (forking.value) return

  forking.value = true
  try {
    const forked = await dashboardStore.forkDashboard(props.dashboard.id, {
      keep_subscription: keepSubscription.value
    })
    props.onFork(forked)
    emit('modal-close')
  } catch (e) {
    toast.error(e.message || 'Failed to fork dashboard')
  } finally {
    forking.value = false
  }
}
</script>

<template>
  <div class="fork-modal">
    <h3 class="modal-title">Fork Dashboard</h3>
    <p class="modal-description">This will create an editable copy of the dashboard in your account.</p>

    <div class="fork-summary">
      <div class="summary-row">
        <span class="summary-label">Name</span>
        <span class="summary-value">{{ dashboard.name }}</span>
      </div>
      <div v-if="dashboard.description" class="summary-row">
        <span class="summary-label">Description</span>
        <span class="summary-value">{{ dashboard.description }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Widgets</span>
        <span class="summary-value">{{ widgets.length }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Saved Searches</span>
        <span class="summary-value">{{ uniqueSavedSearches.length }}</span>
      </div>
    </div>

    <div v-if="uniqueSavedSearches.length" class="saved-searches-list">
      <p class="list-label">Saved searches that will be copied:</p>
      <div
        v-for="ss in uniqueSavedSearches"
        :key="ss.id"
        class="ss-item"
      >
        <span class="ss-name">{{ ss.name }}</span>
        <span class="ss-query">{{ ss.query }}</span>
      </div>
    </div>

    <div v-if="isSubscribed" class="subscription-toggle">
      <label class="toggle-row" @click="keepSubscription = !keepSubscription">
        <span class="toggle-label">
          <font-awesome-icon :icon="['fas', 'link']" class="toggle-icon" />
          Stay subscribed to original
        </span>
        <span class="toggle-switch" :class="{ 'is-active': keepSubscription }">
          <span class="toggle-knob"></span>
        </span>
      </label>
      <p class="toggle-hint">{{ keepSubscription ? 'You will receive updates from the original dashboard' : 'You will only have your forked copy' }}</p>
    </div>

    <div class="modal-actions">
      <button
        class="btn-fork"
        :disabled="forking"
        @click="doFork"
      >
        <font-awesome-icon v-if="forking" :icon="['fas', 'spinner']" spin />
        <font-awesome-icon v-else :icon="['fas', 'code-fork']" />
        {{ forking ? 'Forking...' : 'Fork' }}
      </button>
      <button class="btn-close-modal" @click="emit('modal-close')">
        Cancel
      </button>
    </div>
  </div>
</template>

<style scoped>
.fork-modal {
  width: 500px;
  max-width: 90vw;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #f3f4f6;
  margin: 0 0 0.5rem 0;
}

.modal-description {
  font-size: 0.8125rem;
  color: #9ca3af;
  margin: 0 0 1rem 0;
}

.fork-summary {
  padding: 0.75rem;
  background: rgba(115, 87, 210, 0.06);
  border: 1px solid rgba(115, 87, 210, 0.15);
  border-radius: 0.5rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
}

.summary-row + .summary-row {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.summary-label {
  font-size: 0.8125rem;
  color: #9ca3af;
}

.summary-value {
  font-size: 0.8125rem;
  color: #e5e7eb;
  font-weight: 500;
}

.saved-searches-list {
  margin-top: 0.75rem;
}

.list-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
}

.ss-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.375rem 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.375rem;
}

.ss-item + .ss-item {
  margin-top: 0.375rem;
}

.ss-name {
  font-size: 0.8125rem;
  color: #e5e7eb;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
  max-width: 40%;
}

.ss-query {
  font-size: 0.75rem;
  color: #6b7280;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.btn-fork {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(115, 87, 210, 0.15);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 0.375rem;
  color: #7357D2;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-fork:hover:not(:disabled) {
  background: rgba(115, 87, 210, 0.25);
  border-color: rgba(115, 87, 210, 0.5);
}

.btn-fork:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-close-modal {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  color: #9ca3af;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close-modal:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #d1d5db;
}

.subscription-toggle {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.5rem;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: #e5e7eb;
  font-weight: 500;
}

.toggle-icon {
  font-size: 0.75rem;
  color: #7357D2;
  opacity: 0.7;
}

.toggle-switch {
  position: relative;
  width: 2.25rem;
  height: 1.25rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 0.625rem;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.toggle-switch.is-active {
  background: rgba(115, 87, 210, 0.5);
}

.toggle-knob {
  position: absolute;
  top: 0.125rem;
  left: 0.125rem;
  width: 1rem;
  height: 1rem;
  background: #9ca3af;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.toggle-switch.is-active .toggle-knob {
  left: calc(100% - 1.125rem);
  background: #7357D2;
}

.toggle-hint {
  font-size: 0.6875rem;
  color: #6b7280;
  margin: 0.375rem 0 0 0;
}
</style>
